import { Component, OnInit, OnDestroy, ViewChild, TemplateRef, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer } from '@angular/platform-browser';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import * as alertifyjs from 'alertifyjs';
import Swal from 'sweetalert2';
import { saveAs } from 'file-saver';

import { UserService } from 'src/app/services/user/user.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { PaymentSocketService } from 'src/app/services/socket/payment-socket.service';

/**
 * Vista de tabla paginada de pagos por persona (en paralelo a PersonPaymentsComponent).
 * - Paginación y filtros server-side (endpoint person_payments_console_list).
 * - Filas livianas; el detalle completo se reusa abriendo <app-person-payments> en modo modal.
 * - Quick actions Aprobar/Anular replicando exactamente los guards de la vista clásica.
 */
@Component({
  selector: 'app-person-payments-table',
  templateUrl: './person-payments-table.component.html',
  styleUrls: ['./person-payments-table.component.css']
})
export class PersonPaymentsTableComponent implements OnInit, OnDestroy {

  @ViewChild('detailModal') detailModal!: TemplateRef<any>;

  personCode = '';
  person: any;

  banks: any = {};       // mapa bankCode -> nombre (para "Banco origen")
  bankList: any[] = [];  // lista para el filtro de banco
  productArray: any[] = [];

  rows: any[] = [];
  total = 0;
  page = 1;
  limit = 10;
  pageSizeOptions = [5, 10, 20, 50];
  loading = false;          // carga de filas de la tabla (recargas/paginación)
  loadingInitial = true;    // primera carga completa (persona + primera página)
  loaded = false;           // ya se ejecutó al menos un loadRows

  selectedRow: any = null;
  loadcon: any;   // loading Aprobar
  loadcon2: any;  // loading Anular

  // Visor de comprobante (mismo comportamiento que la vista clásica)
  selectedFileUrl = '';
  selectedFileType = '';
  selectedFileName = '';
  selectedFileExtension = '';

  showProductDropdown = false;
  showFilters = false;   // panel de filtros colapsado por defecto

  filters: any = {
    type: 'all',
    reference: '',
    bank: '',
    amount_min: null,
    amount_max: null,
    init: '',
    end: '',
    selectedProducts: ['CREDIT'] as string[]
  };

  statusOptions = [
    { value: 'all', label: 'Todos' },
    { value: 'p', label: 'Por conciliar' },
    { value: 'pc', label: 'En proceso de validación' },
    { value: 'c', label: 'Aprobado' },
    { value: 'd', label: 'Devolución' },
    { value: 'a', label: 'Anulado / Rechazado / Fallido' }
  ];

  private ownerUpdatesSubscription: any;
  private socketsConfigured = false;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private _user: UserService,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    public _auth: AuthService,
    private paymentSocketService: PaymentSocketService,
    private modalService: NgbModal
  ) { }

  ngOnInit(): void {
    this.activatedRoute.params.subscribe(params => {
      if (params?.['code']) {
        this.personCode = params['code'];
        this.init();
      }
    });
  }

  async init() {
    this.loadingInitial = true;
    await this.getPerson();
    await this.getBanks();
    await this.loadProducts();
    await this.loadRows();
    this.loadingInitial = false;
    this.setupSocket();
  }

  // ============================ Carga base ============================

  getPerson = async () => {
    try {
      const resp = await this._user.getPersonbyCode({ code: this.personCode });
      this.person = resp?.resp;
    } catch (error) {
      console.error(error);
    }
  };

  getBanks = async () => {
    try {
      const resp = await this._user.getBanks({});
      const list = resp?.res || [];
      this.bankList = list;
      const map: any = {};
      list.forEach((v: any) => { map[v.bankCode] = v?.name; });
      this.banks = map;
    } catch (error) {
      console.error(error);
    }
  };

  loadProducts = async () => {
    try {
      const resp = await this._user.getTypesProductsInvoices();
      this.productArray = resp?.resp || [];
    } catch (error) {
      console.error(error);
    }
  };

  // ============================ Listado ============================

  private buildBody() {
    const f = this.filters;
    const body: any = {
      code_person: this.personCode,
      arr_typeProduct: (f.selectedProducts && f.selectedProducts.length) ? f.selectedProducts : ['CREDIT'],
      type: f.type || 'all',
      reference: f.reference || '',
      bank: f.bank || '',
      sortField: 'created_at',
      sortOrder: 'desc'
    };
    if (f.amount_min !== null && f.amount_min !== undefined && `${f.amount_min}` !== '') body.amount_min = f.amount_min;
    if (f.amount_max !== null && f.amount_max !== undefined && `${f.amount_max}` !== '') body.amount_max = f.amount_max;
    return body;
  }

  private buildQuery() {
    const q: any = { page: this.page, limit: this.limit };
    if (this.filters.init) q.init = this.filters.init;
    if (this.filters.end) q.end = this.filters.end;
    return q;
  }

  loadRows = async () => {
    this.loading = true;
    try {
      const resp = await this._user.listClientPaymentsConsolePaginated(this.buildBody(), this.buildQuery());
      this.rows = resp?.res || [];
      this.total = resp?.total || 0;
    } catch (error) {
      console.error(error);
      this.rows = [];
      this.total = 0;
    }
    this.loading = false;
    this.loaded = true;
    this.cdr.detectChanges();
  };

  applyFilters() {
    this.page = 1;
    this.showProductDropdown = false;
    this.loadRows();
  }

  clearFilters() {
    this.filters = {
      type: 'all', reference: '', bank: '', amount_min: null, amount_max: null,
      init: '', end: '', selectedProducts: ['CREDIT']
    };
    this.page = 1;
    this.loadRows();
  }

  // ============================ Filtro de productos ============================

  toggleFilters() { this.showFilters = !this.showFilters; }

  // Cantidad de filtros activos (para el indicador cuando está colapsado)
  get activeFilterCount(): number {
    const f = this.filters;
    let n = 0;
    if (f.type && f.type !== 'all') n++;
    if (f.reference && `${f.reference}`.trim() !== '') n++;
    if (f.bank) n++;
    if (f.amount_min != null && `${f.amount_min}` !== '') n++;
    if (f.amount_max != null && `${f.amount_max}` !== '') n++;
    if (f.init) n++;
    if (f.end) n++;
    if (!(f.selectedProducts?.length === 1 && f.selectedProducts[0] === 'CREDIT')) n++;
    return n;
  }

  toggleProductDropdown() { this.showProductDropdown = !this.showProductDropdown; }

  isProductSelected(code: string) { return this.filters.selectedProducts.includes(code); }

  toggleProduct(code: string, checked: boolean) {
    if (checked) {
      if (!this.filters.selectedProducts.includes(code)) this.filters.selectedProducts.push(code);
    } else {
      this.filters.selectedProducts = this.filters.selectedProducts.filter((c: string) => c !== code);
    }
  }

  get productLabel(): string {
    const sel = this.filters.selectedProducts;
    if (!sel || sel.length === 0) return 'Todos los productos';
    if (sel.length === 1) return sel[0];
    return `${sel.length} productos`;
  }

  // ============================ Paginación ============================

  get totalPages(): number { return Math.max(1, Math.ceil(this.total / this.limit)); }

  get rangeStart(): number { return this.total === 0 ? 0 : ((this.page - 1) * this.limit) + 1; }
  get rangeEnd(): number { return Math.min(this.total, this.page * this.limit); }

  goToPage(p: number) {
    if (p < 1 || p > this.totalPages || p === this.page) return;
    this.page = p;
    this.loadRows();
  }

  changePageSize(size: any) {
    this.limit = Number(size) || 10;
    this.page = 1;
    this.loadRows();
  }

  get pageNumbers(): number[] {
    const pages: number[] = [];
    const tp = this.totalPages;
    let start = Math.max(1, this.page - 2);
    let end = Math.min(tp, start + 4);
    start = Math.max(1, end - 4);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }

  trackByCode = (_: number, row: any) => row?.payment?.code;

  // ============================ Estatus (fiel a person-payments.component.html:65-95) ============================

  getStatusLabel(p: any): string {
    // status === false tiene prioridad (igual que el bloque !status independiente
    // de la vista clásica): cubre pagos conciliados pero fallidos (AUTO), anulados, etc.
    if (!p?.status) {
      if (p?.typeTransaction !== 'DOMICILIATION') {
        return p?.typeTransaction === 'AUTO' ? 'Transacción fallida' : 'Anulado';
      }
      return 'Rechazado por el Banco';
    }
    // status === true
    if (p?.reconciled_payment) {
      if (p?.payment_annulled) return 'Devolución';
      if (p?.typeTransaction !== 'DOMICILIATION') return 'Aprobado';
      if (p?.amount === 0) return p?.description || 'Rechazado';
      return 'Aprobado';
    }
    return p?.typeTransaction !== 'PENDING' ? 'Por conciliar' : 'En proceso de validación';
  }

  getStatusClass(p: any): string {
    if (!p?.status) return 'text-bg-danger';
    if (p?.reconciled_payment) {
      if (p?.payment_annulled) return 'text-bg-primary';
      if (p?.typeTransaction !== 'DOMICILIATION') return 'text-bg-success';
      if (p?.amount === 0) return 'text-bg-danger';
      return 'text-bg-success';
    }
    return 'text-bg-warning';
  }

  bankName(p: any): string { return (p?.bankCode && this.banks[p.bankCode]) || '-'; }

  /** Es un pago de cuota inicial (order 0) que no tiene banco/método que mostrar. */
  isInicial(p: any): boolean {
    if (!p?.is_initial) return false;
    const hasBank = !!(p?.bankCode && this.banks[p.bankCode]);
    const hasMethod = !!p?.account_payment?.payment_method?.name;
    return !hasBank && !hasMethod;
  }

  // ============================ Detalle (reuso en modo modal) ============================

  openDetail(row: any) {
    this.selectedRow = row;
    // Solo abrir el detalle. NO recargamos al cerrar: si dentro del modal se
    // aprueba/anula, el socket (specificOwnerUpdates -> loadRows) refresca la
    // tabla automáticamente, así evitamos el loading innecesario al cerrar.
    this.modalService.open(this.detailModal, { size: 'xl', scrollable: true });
  }

  // ============================ Visor de comprobante (copia fiel de la vista clásica) ============================

  async redirectIMG(fileData: any, modalContent?: any): Promise<void> {
    let url: string;
    let fileType: string;
    let fileName: string;

    if (typeof fileData === 'string') {
      url = fileData;
      fileType = url.toLowerCase().includes('.pdf') ? 'pdf' : 'image';
      fileName = `comprobante_${Date.now()}`;
    } else if (fileData && fileData.url) {
      url = fileData.url;
      fileType = fileData.type_file || 'pdf';
      fileName = fileData.base_name || `comprobante_${Date.now()}`;
    } else {
      alertifyjs.error('Este pago no tiene comprobante adjunto');
      return;
    }

    if (!url) return;

    this.selectedFileUrl = url;
    this.selectedFileName = fileName;
    this.selectedFileExtension = fileType;

    const isPdf = fileType.toLowerCase() === 'pdf';

    if (isPdf) {
      const fileNameWithExt = fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`;
      const blob = await this._user.downloadFile(url, fileType);
      if (blob) {
        saveAs(blob, fileNameWithExt);
      } else {
        alertifyjs.error('Error al descargar el archivo');
        window.open(url, '_blank');
      }
    } else {
      this.selectedFileType = 'image';
      if (modalContent) {
        this.modalService.open(modalContent, { size: 'xl', centered: true, backdrop: 'static' });
      }
    }
  }

  async onImageError(): Promise<void> {
    this.closeFileModal();
    const fileName = this.selectedFileName || `comprobante_${Date.now()}`;
    const fileExtension = this.selectedFileExtension || 'pdf';
    const fileNameWithExt = fileName.endsWith(`.${fileExtension}`) ? fileName : `${fileName}.${fileExtension}`;
    const blob = await this._user.downloadFile(this.selectedFileUrl, fileExtension);
    if (blob) {
      saveAs(blob, fileNameWithExt);
    } else {
      alertifyjs.error('Error al descargar el archivo');
      window.open(this.selectedFileUrl, '_blank');
    }
  }

  async downloadCurrentFile(): Promise<void> {
    const fileName = this.selectedFileName || `comprobante_${Date.now()}`;
    const fileExtension = this.selectedFileExtension || 'jpg';
    const fileNameWithExt = fileName.endsWith(`.${fileExtension}`) ? fileName : `${fileName}.${fileExtension}`;
    const blob = await this._user.downloadFile(this.selectedFileUrl, fileExtension);
    if (blob) {
      saveAs(blob, fileNameWithExt);
    } else {
      alertifyjs.error('Error al descargar el archivo');
    }
  }

  closeFileModal(): void {
    this.selectedFileUrl = '';
    this.selectedFileType = '';
    this.selectedFileName = '';
    this.selectedFileExtension = '';
    this.modalService.dismissAll();
  }

  // ============================ Quick actions (guards idénticos a la vista clásica) ============================

  canShowActions(p: any): boolean {
    return this._auth.hasAccessOrProfile('finanzas', ['OWNER', 'ANALIST'])
      && !p?.reconciled_payment && p?.status && p?.typeTransaction !== 'PENDING';
  }

  canApprove(): boolean {
    return this._auth.hasAnyPermission(['finanzas.verificacion_pagos.approve', 'pagos.pagos_registrados.approve'])
      || !this._auth.hasRbac$.getValue();
  }

  canCancel(): boolean {
    return this._auth.hasAnyPermission(['finanzas.verificacion_pagos.cancel', 'pagos.pagos_registrados.cancel'])
      || !this._auth.hasRbac$.getValue();
  }

  canEdit(): boolean {
    return !this._auth.hasRbac$.getValue() || this._auth.hasAnyPermission(['pagos.pagos_registrados.update']);
  }

  // Copia fiel de setPayment (person-payments.component.ts): edición de pago.
  setPayment(data: any) {
    if (this._auth.hasRbac$.getValue() && !this._auth.hasAnyPermission(['pagos.pagos_registrados.update'])) {
      alertifyjs.error('No tienes permiso para editar pagos');
      return;
    }

    const action = 'Editar';

    Swal.fire({
      title: `¿Deseas ${action} este pago?`,
      text: '¡No podrás revertir esto!',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      input: 'select',
      inputOptions: {
        'Banco Receptor errado': 'Banco Receptor errado',
        'Banco Emisor errado': 'Banco Emisor errado',
        'Número de referencia errado': 'Número de referencia errado',
        'Fecha de Pago errada': 'Fecha de Pago errada',
        'Monto errado': 'Monto errado',
        'Comprobante de pago errado': 'Comprobante de pago errado',
        'Panalidades mal aplicadas': 'Penalidades mal aplicadas'
      },
      inputPlaceholder: 'Selecciona una razón',
      showCancelButton: true,
      confirmButtonText: `Si, ${action}!`,
      showLoaderOnConfirm: true,
      preConfirm: async (reason) => {
        try {
          if (!reason) return Swal.showValidationMessage(`Debe seleccionar una razón de Edición`);
        } catch (error) {
          Swal.showValidationMessage(`Request failed: ${error}`);
        }
      },
      allowOutsideClick: () => !Swal.isLoading()
    }).then((result) => {
      if (result.isConfirmed) {
        data.description_edit = result?.value;
        // La vista de pagos POR PERSONA no incluye owner.code en cada row (la persona es implícita
        // por la URL). El form de edición (post) lo necesita en pe.payment.owner.code para cargar la
        // persona y la tasa; sin él queda roto ("Null 10,000.00"). Lo inyectamos desde personCode.
        // OJO: owner puede venir como string (_id) → hay que normalizarlo a objeto, no setear .code
        // sobre un string (eso lanza "Cannot create property 'code' on string").
        if (data?.payment) {
          const ownerVal = data.payment.owner;
          if (ownerVal && typeof ownerVal === 'object') {
            if (!ownerVal.code) ownerVal.code = this.personCode;
          } else {
            data.payment.owner = { _id: typeof ownerVal === 'string' ? ownerVal : undefined, code: this.personCode };
          }
        }
        localStorage.setItem('pe', JSON.stringify(data));
        this.router.navigate([`/console/post`], {});
      }

    });
  }

  // Copia fiel de concilidarPago (person-payments.component.ts:466-510), sin el debugger.
  concilidarPago = async (payment: any) => {
    if (this._auth.hasRbac$.getValue() && !this._auth.hasAnyPermission(['finanzas.verificacion_pagos.approve', 'pagos.pagos_registrados.approve'])) {
      alertifyjs.error('No tienes permiso para aprobar pagos');
      return;
    }
    let confirm = false;
    let action = 'Conciliar';
    // verifico que el pago no este repetido, sino, emitimos alerta
    if (payment?.payments_similar && payment.payments_similar.length > 0) {
      Swal.fire({
        title: `Ya existe un pago con la misma referencia, cuenta destino y monto`,
        text: '¡No es posible conciliar!',
        icon: 'warning'
      }).then((result) => {
        if (result.isConfirmed) {
          confirm = false;
        }
      });
    } else {
      confirm = true;
    }

    if (confirm) {
      Swal.fire({
        title: `¿Deseas ${action} este pago?`,
        text: '¡No podrás revertir esto!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: `Si, ${action}!`
      }).then((result) => {
        if (result.isConfirmed) {
          this.concilidarPagoQuestion(payment?.code);
        }
      });
    }
  };

  concilidarPagoQuestion = async (code: any) => {
    this.loadcon = true;
    try {
      const resp = await this._user.reconciledPayment({ code_payment: code });
      if (resp?.res) {
        alertifyjs.success(`Pago conciliado código ${code}`);
      }
      await this.loadRows();
      this.loadcon = false;
    } catch (error) {
      console.error(error);
      this.loadcon = false;
    }
  };

  // Copia fiel de cancelPayment (person-payments.component.ts:287-329).
  cancelPayment = async (code: any) => {
    if (this._auth.hasRbac$.getValue() && !this._auth.hasAnyPermission(['finanzas.verificacion_pagos.cancel', 'pagos.pagos_registrados.cancel'])) {
      alertifyjs.error('No tienes permiso para anular pagos');
      return;
    }

    let action = 'Anular';

    Swal.fire({
      title: '¿Deseas Anular este pago?',
      text: '¡No podrás revertir esto!',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      input: 'select',
      inputOptions: {
        'No se encuentra en banco': 'No se encuentra en banco',
        'Pago duplicado': 'Pago duplicado',
        'Txs verificada por Merchant': 'Txs verificada por Merchant',
        'Txs verificada manualmente': 'Txs verificada manualmente'
      },
      inputPlaceholder: 'Selecciona una razón',
      showCancelButton: true,
      confirmButtonText: `Si, ${action}!`,
      showLoaderOnConfirm: true,
      preConfirm: async (reason) => {
        try {
          if (!reason) {
            return Swal.showValidationMessage(`Debe seleccionar una razón de anulación`);
          }
        } catch (error) {
          Swal.showValidationMessage(`Request failed: ${error}`);
        }
      },
      allowOutsideClick: () => !Swal.isLoading()
    }).then((result) => {
      if (result.isConfirmed) {
        this.cancelPaymentQuestion(code, result.value);
      }
    });
  };

  cancelPaymentQuestion = async (code: any, msg = '') => {
    this.loadcon2 = true;
    try {
      const resp = await this._user.cancelPayment({ code_payment: code, description: msg });
      if (resp?.res) {
        alertifyjs.success(`Pago anulado codigo ${code}`);
      }
      await this.loadRows();
      this.loadcon2 = false;
    } catch (error) {
      console.error(error);
      this.loadcon2 = false;
    }
  };

  // ============================ Socket (refresca página actual) ============================

  private setupSocket(): void {
    if (this.socketsConfigured) return;
    if (this.person?.person?._id) {
      this.paymentSocketService.listenToOwnerUpdates(this.person.person._id);
      this.ownerUpdatesSubscription = this.paymentSocketService.specificOwnerUpdates.subscribe((ownerUpdate: any) => {
        if (ownerUpdate && ownerUpdate.ownerId === this.person.person._id) {
          this.loadRows().then(() => this.cdr.detectChanges());
        }
      });
      this.socketsConfigured = true;
    }
  }

  ngOnDestroy(): void {
    if (this.ownerUpdatesSubscription) {
      this.ownerUpdatesSubscription.unsubscribe();
      this.ownerUpdatesSubscription = null;
    }
    if (this.person?.person?._id) {
      this.paymentSocketService.cleanupOwnerListeners(this.person.person._id);
    }
    this.socketsConfigured = false;
  }

  // ============================ Header helpers (reusados de la vista clásica) ============================

  getIdentity(item: any) {
    if (!(item?.identitys?.length > 0)) return '';
    return `${item?.identitys[0]?.type_identity?.name} ${item?.identitys[0]?.code_identity?.name}-${item?.identitys[0]?.identity}`;
  }

  getContact(item: any) {
    if (!(item?.constacts?.length > 0)) return '';
    return `${item?.constacts[0]?.contact}`;
  }

  getEmailContact(item: any) {
    if (!(item?.constacts?.length > 0) || !item?.constacts[1]) return '';
    return `${item?.constacts[1]?.contact}`;
  }

  sanitize(url: string) { return this.sanitizer.bypassSecurityTrustUrl(url); }

  // ============================ Navegación ============================

  goClassic() {
    this.router.navigate(['/console/payments/person', this.personCode]);
  }
}
